import { useEffect, useMemo, useRef } from "react";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useMembers } from "@/lib/hooks/useMembers";
import { addStreakShieldRescues } from "@/lib/db/members";
import { calculateStreakWithShield } from "@/lib/utils/streaks";
import { startOfThisWeek } from "@/lib/utils/dates";

export type StreakStatus = {
  streak: number;
  shieldAvailable: boolean;
  /** True jeśli ostatnie liczenie zużyło tarczę i to spowodowało zapis. */
  shieldJustConsumed: boolean;
};

/**
 * Liczy streak z mechaniką tarczy + atomowo zapisuje uratowane dni do
 * member.streakShieldRescues, żeby kolejne renderowania traktowały je
 * jak normalne wykonania.
 *
 * Dlaczego side-effect tutaj?
 * Tarcza musi być „lepka" — raz uratowany dzień zostaje uratowany na
 * zawsze. Bez zapisu, każdy re-render z nową kalkulacją mógłby albo
 * stracić uratowanie (gdy tarcza zużyta tego tygodnia z innej akcji),
 * albo migotać w UI.
 */
export function useStreakStatus(
  hid: string | undefined,
  uid: string | undefined,
): StreakStatus {
  const { completions: ownApproved } = useCompletions(hid, {
    status: "approved",
    completedBy: uid,
  });
  const { members } = useMembers(hid);
  const me = members.find((m) => m.userId === uid);
  const rescues = useMemo(
    () => (me?.streakShieldRescues ?? []).map((t) => t.toDate()),
    [me?.streakShieldRescues],
  );

  const result = useMemo(() => {
    const weekStart = startOfThisWeek().getTime();
    const shieldAvailable = !rescues.some((d) => d.getTime() >= weekStart);
    return calculateStreakWithShield(ownApproved, rescues, shieldAvailable);
  }, [ownApproved, rescues]);

  // Zapis nowych uratowań — tylko raz na konkretny dzień.
  // useRef zabezpiecza przed wielokrotnym writem przy szybkich re-renderach.
  const lastWrittenKey = useRef<string>("");
  useEffect(() => {
    if (!hid || !uid) return;
    if (result.newRescues.length === 0) return;
    const key = result.newRescues
      .map((d) => d.getTime())
      .sort()
      .join(",");
    if (key === lastWrittenKey.current) return;
    lastWrittenKey.current = key;
    void addStreakShieldRescues(hid, uid, result.newRescues).catch(() => {
      // jeśli się nie uda — pozwól na ponowną próbę przy następnej zmianie
      lastWrittenKey.current = "";
    });
  }, [hid, uid, result.newRescues]);

  return {
    streak: result.streak,
    shieldAvailable: result.shieldAvailable && result.newRescues.length === 0,
    shieldJustConsumed: result.newRescues.length > 0,
  };
}
